// opensAt/closesAt are "HH:mm:ss" strings in India local time (all restaurants are in India).
// Either being null/missing means hours are unknown, so treat the restaurant as open.
const RESTAURANT_TIME_ZONE = "Asia/Kolkata";

const toMinutes = (time) => {
  const [h, m, s] = time.split(":").map(Number);
  if ([h, m].some(Number.isNaN)) return null;
  return h * 60 + m + (s || 0) / 60;
};

// Device/browser local time can be in any timezone, but restaurant hours are always
// India time, so we can't use now.getHours()/getMinutes() directly.
const nowMinutesInIndia = (now) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: RESTAURANT_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);

  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return get("hour") * 60 + get("minute") + get("second") / 60;
};

export const isRestaurantClosed = (restaurant, now = new Date()) => {
  const { opensAt, closesAt } = restaurant || {};
  if (!opensAt || !closesAt) return false;

  const opensAtMin = toMinutes(opensAt);
  const closesAtMin = toMinutes(closesAt);
  if (opensAtMin === null || closesAtMin === null) return false;

  const nowMin = nowMinutesInIndia(now);

  if (closesAtMin <= opensAtMin) {
    // Overnight window, e.g. opens 18:00, closes 02:00.
    return nowMin >= closesAtMin && nowMin < opensAtMin;
  }

  return nowMin < opensAtMin || nowMin >= closesAtMin;
};

export const CLOSING_SOON_THRESHOLD_MINUTES = 30;

// Minutes from now until the restaurant closes, or null if it's closed / has no hours.
const getMinutesToClose = (restaurant, now = new Date()) => {
  const { opensAt, closesAt } = restaurant || {};
  if (!opensAt || !closesAt) return null;
  if (isRestaurantClosed(restaurant, now)) return null;

  const opensAtMin = toMinutes(opensAt);
  const closesAtMin = toMinutes(closesAt);
  if (opensAtMin === null || closesAtMin === null) return null;

  const nowMin = nowMinutesInIndia(now);

  if (closesAtMin <= opensAtMin && nowMin >= opensAtMin) {
    // Overnight window, currently on the "before midnight" side, e.g. opens 18:00, closes 02:00.
    return 24 * 60 - nowMin + closesAtMin;
  }

  return closesAtMin - nowMin;
};

// Returns a display message like "Closing in 15 mins" when the restaurant is open
// and closes within `thresholdMinutes`, otherwise null.
export const getClosingSoonMessage = (
  restaurant,
  now = new Date(),
  thresholdMinutes = CLOSING_SOON_THRESHOLD_MINUTES
) => {
  const minutesToClose = getMinutesToClose(restaurant, now);
  if (minutesToClose === null || minutesToClose <= 0 || minutesToClose > thresholdMinutes) {
    return null;
  }
  const mins = Math.max(1, Math.round(minutesToClose));
  return `Closing in ${mins} min${mins === 1 ? "" : "s"}`;
};

// Stable sort: open restaurants keep their original relative order, closed ones
// keep theirs too, but all closed restaurants move after all open ones.
export const sortRestaurantsByOpenStatus = (restaurants, now = new Date()) => {
  return [...restaurants].sort(
    (a, b) => (isRestaurantClosed(a, now) ? 1 : 0) - (isRestaurantClosed(b, now) ? 1 : 0)
  );
};

// "07:00:00" -> "7:00 AM", for display next to a closed/opening-hours message.
export const formatHourLabel = (time) => {
  if (!time) return null;
  const [hStr, mStr] = time.split(":");
  const h = Number(hStr);
  if (Number.isNaN(h) || mStr === undefined) return null;
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${mStr} ${period}`;
};
